/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * A cooking group — the feed.
 *
 * ── WHAT THIS SCREEN BECAME ─────────────────────────────────────────────────
 * It was a chat window: a bordered scroller capped at 60vh, messages as list
 * rows, 96px photo thumbnails, your own household's posts tinted with a
 * background wash, and a three-field composer whose FIRST control was a
 * monospace "suggested title" input with a paragraph of instructions.
 *
 * It is now a feed. The unit is a post, the photo leads, reactions and replies
 * live on the card, and the composer is one line until you tap it. The changes
 * that matter:
 *
 *   • ORDER. Newest first. A chat reads oldest-first because you follow a
 *     conversation; a feed reads newest-first because you catch up on one.
 *   • NO SELF-TINT. In a group of six families "who said this" matters more
 *     than "was it me", so identity is an avatar and a name, not a wash.
 *   • NO INNER SCROLLER. The page scrolls. A 60vh box inside a scrolling page
 *     is two scroll contexts fighting, which on a phone means neither works.
 *   • SHARED RECIPES ABOVE THE FEED. Posts are deleted after the retention
 *     window; a recipe someone gave you is permanent. The lasting thing should
 *     not be the one hidden behind a header button.
 *
 * ── THE LINE THIS DOES NOT CROSS ────────────────────────────────────────────
 * Migration 027 is explicit that a message's `suggested_title` is a NAME and
 * never a recipe_id — naming a dish is not granting a read. Nothing here
 * changes that. A post that names a dish says so on its own card, and the only
 * recipes that can actually be copied are the ones a household deliberately
 * shared through 029's library grant.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { CirclesApi } from "../../api/endpoints.ts";
import type { Circle, CircleMember, CircleMessage, CircleLibrarySettings, AvailableLibraryRecipe, CircleInvite, CreatedInvite } from "../../api/types.ts";
import { Empty, ErrorBox, Loading, PageHeader, Banner, Modal } from "../../components/ui.tsx";
import { Icon } from "../../components/Icon.tsx";
import { ActionMenu } from "../../components/ActionMenu.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";
import { PostCard } from "./PostCard.tsx";
import { Composer } from "./Composer.tsx";
import { SharedRecipeStrip } from "./SharedRecipeStrip.tsx";

/** A stable colour per household, so the same name looks the same each time. */
function memberTone(label: string): string {
  const tones = [
    "bg-accent/15 text-accent",
    "bg-teal/15 text-teal",
    "bg-navy/15 text-navy",
    "bg-ok/15 text-ok",
    "bg-warn/20 text-ink-2",
  ];
  let hash = 0;
  for (let i = 0; i < label.length; i++) hash = (hash * 31 + label.charCodeAt(i)) | 0;
  return tones[Math.abs(hash) % tones.length]!;
}

export function CircleDetailView() {
  const { t } = usePrefs();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [lightbox, setLightbox] = useState<string | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showInvites, setShowInvites] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const circleQuery = useQuery({
    queryKey: ["circle", id],
    queryFn: () => CirclesApi.get(id!),
    retry: false,
  });

  const membersQuery = useQuery({
    queryKey: ["circle-members", id],
    queryFn: () => CirclesApi.members(id!),
    retry: false,
  });

  const messagesQuery = useQuery({
    queryKey: ["circle-messages", id],
    queryFn: () => CirclesApi.messages(id!, undefined, 50),
    retry: false,
  });

  // Library sharing
  const librarySettingsQuery = useQuery({
    queryKey: ["circle-library-settings", id],
    queryFn: () => CirclesApi.library.settings.get(id!),
    retry: false,
  });

  // No longer gated on the modal being open: SharedRecipeStrip renders this
  // same query key on the page itself, so the data is fetched once and both the
  // strip and the modal read it.
  const libraryRecipesQuery = useQuery({
    queryKey: ["circle-library-recipes", id],
    queryFn: () => CirclesApi.library.recipes.list(id!),
    retry: false,
  });

  const setLibrarySharing = useMutation({
    mutationFn: (enabled: boolean) => CirclesApi.library.settings.set(id!, enabled),
    onSuccess: (_result, enabled: boolean) => {
      void qc.invalidateQueries({ queryKey: ["circle-library-settings", id] });
      // Toggling changes what everyone in the circle can see, including the
      // "already accepted" flags — without this the list stayed as whatever it
      // was when the modal opened.
      void qc.invalidateQueries({ queryKey: ["circle-library-recipes", id] });
      // The strip reads the same shelf with includeOwn=true under its own key.
      void qc.invalidateQueries({ queryKey: ["circle-library-shelf", id] });
      toast(t(enabled ? "circles.librarySharingEnabled" : "circles.librarySharingDisabled"), "ok");
    },
    onError: (e) => toast(e instanceof Error ? e.message : t("common.error"), "danger"),
  });

  const acceptLibraryRecipe = useMutation({
    mutationFn: (recipeId: string) => CirclesApi.library.recipes.accept(id!, recipeId),
    onSuccess: () => {
      toast(t("circles.recipeAccepted"), "ok");
      void qc.invalidateQueries({ queryKey: ["circle-library-recipes", id] });
      void qc.invalidateQueries({ queryKey: ["circle-library-shelf", id] });
      void qc.invalidateQueries({ queryKey: ["recipes"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : t("common.error"), "danger"),
  });

  const markRead = useMutation({
    mutationFn: (revision: number) => CirclesApi.markRead(id!, revision),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["circle", id] });
    },
    onError: (e) => console.warn("Failed to mark read:", e),
  });

  const leaveCircle = useMutation({
    mutationFn: () => CirclesApi.leave(id!),
    onSuccess: () => {
      toast(t("circles.left"), "ok");
      navigate("/circles");
      void qc.invalidateQueries({ queryKey: ["circles"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : t("common.error"), "danger"),
  });

  const setMuted = useMutation({
    mutationFn: (muted: boolean) => CirclesApi.setMuted(id!, muted),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["circle", id] });
      void qc.invalidateQueries({ queryKey: ["circles"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : t("common.error"), "danger"),
  });

  const setDisplayLabel = useMutation({
    mutationFn: (label: string) => CirclesApi.setDisplayLabel(id!, label),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["circle", id] });
      void qc.invalidateQueries({ queryKey: ["circle-members", id] });
      toast(t("circles.displayNameSaved"), "ok");
    },
    onError: (e) => toast(e instanceof Error ? e.message : t("common.error"), "danger"),
  });

  // ── Invites ───────────────────────────────────────────────────────────
  //
  // These MUST live above the early returns below. React identifies hooks by
  // call order, so a hook that only runs once `circleQuery` has resolved
  // changes the hook count between renders and React throws — which, with no
  // error boundary above it, unmounts the whole app and leaves a blank page.
  const [createdInvite, setCreatedInvite] = useState<CreatedInvite | null>(null);

  const invitesQuery = useQuery({
    queryKey: ["circle-invites", id],
    queryFn: () => CirclesApi.listInvites(id!),
    retry: false,
    enabled: Boolean(id) && showInvites,
  });

  const createInvite = useMutation({
    mutationFn: (maxUses?: number) => CirclesApi.createInvite(id!, maxUses),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["circle-invites", id] });
      // Close the invites list so nothing sits on top of the code, which the
      // server returns exactly once and can never show again.
      setShowInvites(false);
      setCreatedInvite(result);
      toast(t("circles.inviteCodeCreated"), "ok");
    },
    onError: (e) => toast(e instanceof Error ? e.message : t("common.error"), "danger"),
  });

  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) => CirclesApi.revokeInvite(id!, inviteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["circle-invites", id] });
      toast(t("circles.inviteCodeRevoked"), "ok");
    },
    onError: (e) => toast(e instanceof Error ? e.message : t("common.error"), "danger"),
  });

  // Mark messages as read.
  //
  // `markRead` is a fresh object every render, so keeping it in a dependency
  // array re-fires the effect on every render and, while anything is unread,
  // turns into an unbounded POST loop. Read it through a ref instead, and
  // remember the highest revision already sent so a slow round-trip can't
  // trigger a second send for the same revision.
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  const sentRevisionRef = useRef(0);

  const syncRead = useCallback(() => {
    const msgs = messagesQuery.data?.messages ?? [];
    const circleData = circleQuery.data;
    const lastMsg = msgs[msgs.length - 1];
    if (!circleData || !lastMsg) return;
    if (circleData.lastReadRevision >= lastMsg.revision) return;
    if (sentRevisionRef.current >= lastMsg.revision) return;
    sentRevisionRef.current = lastMsg.revision;
    markReadRef.current.mutate(lastMsg.revision);
  }, [circleQuery.data, messagesQuery.data]);

  useEffect(() => { syncRead(); }, [syncRead]);

  useEffect(() => {
    const interval = setInterval(syncRead, 30000);
    return () => clearInterval(interval);
  }, [syncRead]);

  // Scroll to TOP on arrival.
  //
  // This used to scroll to the bottom, which was right for a chat — the newest
  // message was last, and you wanted to be looking at it. The feed is
  // newest-first, so the newest post is at the top and the old behaviour would
  // now dump the reader at the oldest post in the group.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  const refreshAll = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["circle", id] });
    void qc.invalidateQueries({ queryKey: ["circle-members", id] });
    void qc.invalidateQueries({ queryKey: ["circle-messages", id] });
  }, [qc, id]);

  if (!id) return <ErrorBox message={t("circles.notFound")} />;
  if (circleQuery.isLoading) return <Loading label={t("circles.loading")} />;
  if (circleQuery.error) {
    return (
      <ErrorBox
        message={(circleQuery.error as Error).message}
        onRetry={refreshAll}
      />
    );
  }

  const circle: Circle | undefined = circleQuery.data;
  if (!circle) return <ErrorBox message={t("circles.notFound")} />;

  const members: CircleMember[] = membersQuery.data?.members ?? [];
  const messages: CircleMessage[] = messagesQuery.data?.messages ?? [];

  const myMember = members.find((m) => m.isSelf);
  const isOwner = circle.isOwner;
  const canPost = !circle.isLocked || isOwner;

  // The feed shows top-level posts only, newest first.
  //
  // Two changes from the old list, both deliberate. Replies are excluded —
  // they belong inside their parent's card, not scattered through the timeline
  // as unattached lines, which is exactly the problem migration 032 was written
  // to fix. And the order is reversed: a chat reads oldest-first because you
  // are following a conversation, but a feed reads newest-first because you are
  // catching up on one.
  const posts = messages
    .filter((m) => !m.replyTo)
    .slice()
    .reverse();

  return (
    <div className="animate-fade-in">
      <PageHeader
        title={circle.name}
        subtitle={`${members.length} ${members.length === 1 ? "household" : "households"} · ${
          circle.messageCount} ${circle.messageCount === 1 ? "post" : "posts"}`}
        actions={
          <ActionMenu
            label="Group settings"
            items={[
              {
                label: circle.isMuted ? t("circles.unmute") : t("circles.mute"),
                icon: circle.isMuted ? "chat" : "alert",
                hint: circle.isMuted ? undefined : "Stop counting unread posts",
                onSelect: () => setMuted.mutate(!circle.isMuted),
              },
              {
                label: t("circles.librarySharing"),
                icon: "book",
                hint: "Choose whether this group can copy your recipes",
                onSelect: () => setShowLibrary(true),
              },
              ...(isOwner
                ? [{
                    label: t("circles.createInviteCode"),
                    icon: "users" as const,
                    hint: "Invite another household",
                    onSelect: () => setShowInvites(true),
                  }]
                : []),
              {
                label: "Change how you appear here",
                icon: "person",
                hint: myMember?.label ?? undefined,
                onSelect: () => {
                  const next = window.prompt(t("circles.displayName"), myMember?.label ?? "");
                  if (next) setDisplayLabel.mutate(next);
                },
              },
              {
                label: t("circles.leave"),
                icon: "logout",
                destructive: true,
                onSelect: () => {
                  if (window.confirm(t("circles.leaveConfirm"))) leaveCircle.mutate();
                },
              },
            ]}
          />
        }
      />

      {/* ── who's here ──────────────────────────────────────────────────────
          A row of initials rather than the old right-hand column of monospace
          labels. On a phone that column was pushed below the entire message
          list, so the answer to "who is in this group?" sat at the bottom of a
          scroll; here it is one line under the title, on every screen size. */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {members.map((m) => (
          <span
            key={m.id}
            title={`${m.label}${m.role === "owner" ? " · owner" : ""}${m.isSelf ? " · you" : ""}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-rule-soft bg-paper-2 py-0.5 pl-0.5 pr-2.5 text-xs"
          >
            <span aria-hidden className={`grid h-6 w-6 place-items-center rounded-full text-[10px] font-semibold ${
              memberTone(m.label)}`}>
              {m.label.trim().charAt(0).toUpperCase() || "?"}
            </span>
            <span className="max-w-[8rem] truncate text-ink-2">{m.label}</span>
            {m.role === "owner" && <Icon name="crown" className="h-3 w-3 shrink-0 text-ink-3" />}
          </span>
        ))}
      </div>

      {circle.isLocked && (
        <div className="mb-4">
          <Banner tone="warn">{t("circles.lockedInfo")}</Banner>
        </div>
      )}

      {/* ── shared recipes ────────────────────────────────────────────────
          Above the feed on purpose. Posts are deleted after the retention
          window; a recipe someone gave you is permanent. The lasting thing
          should not be the one buried behind a header button. */}
      <div className="mb-5">
        <SharedRecipeStrip circleId={circle.id} onManage={() => setShowLibrary(true)} />
      </div>

      {/* ── compose ───────────────────────────────────────────────────────── */}
      <div className="mb-4">
        <Composer
          circleId={circle.id}
          canPost={canPost}
          lockedReason={circle.isLocked ? t("circles.lockedInfo") : undefined}
          onManage={() => setShowLibrary(true)}
        />
      </div>

      {/* ── the feed ──────────────────────────────────────────────────────── */}
      {posts.length === 0 ? (
        <Empty
          title="Nothing here yet"
          hint="Post a photo of something you've cooked. It's the easiest way to get a group
                going, and the others will see it next time they open the app."
          action={null}
        />
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              circleId={circle.id}
              canPost={canPost}
              isOwner={isOwner}
              onLightbox={setLightbox}
            />
          ))}
        </div>
      )}

      <p className="mt-6 text-center text-xs text-ink-3">
        {t("circles.retentionHint").replace("{days}", String(circle.retentionDays))}
      </p>
      <div ref={messagesEndRef} />


      {showInvites && (
        <InvitesModal
          invites={invitesQuery.data?.invites ?? []}
          isLoading={invitesQuery.isLoading}
          isCreating={createInvite.isPending}
          onClose={() => setShowInvites(false)}
          onCreate={(maxUses) => createInvite.mutate(maxUses)}
          onRevoke={(inviteId) => revokeInvite.mutate(inviteId)}
        />
      )}
      {showLibrary && (
        <LibrarySharingModal
          settings={librarySettingsQuery.data}
          recipes={libraryRecipesQuery.data?.recipes ?? []}
          isTogglePending={setLibrarySharing.isPending || librarySettingsQuery.isLoading}
          isLoadingRecipes={libraryRecipesQuery.isLoading}
          acceptingId={acceptLibraryRecipe.isPending ? acceptLibraryRecipe.variables : null}
          onClose={() => setShowLibrary(false)}
          onToggleSharing={(enabled) => setLibrarySharing.mutate(enabled)}
          onAcceptRecipe={(recipeId) => acceptLibraryRecipe.mutate(recipeId)}
        />
      )}

      {/* Full-size view. Reuses the object URL the thumbnail already holds, so
          opening a photo costs no second fetch and nothing extra to revoke. */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center bg-ink/80 p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={t("circles.viewPhoto")}
        >
          <img
            src={lightbox}
            alt=""
            className="max-h-full max-w-full rounded-lg object-contain"
          />
          <button
            className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-paper text-ink"
            onClick={() => setLightbox(null)}
            aria-label={t("common.close")}
          >
            <Icon name="x" className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* LAST on purpose. Every modal is `fixed inset-0 z-50`, so with equal
          z-index the later sibling paints on top. Rendered before the invites
          modal, the one-time invite code was covered by the invites list and
          the user never saw it. */}
      {createdInvite && (
        <CreatedInviteModal
          invite={createdInvite}
          onClose={() => setCreatedInvite(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function CreatedInviteModal({ invite, onClose }: {
  invite: CreatedInvite;
  onClose: () => void;
}) {
  const { t } = usePrefs();
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(invite.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal title={t("circles.inviteCodeCreated")} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="card p-4 text-center">
          <p className="text-sm text-ink-3 mb-2">{t("circles.shareThisCode")}</p>
          <div className="flex items-center justify-center gap-2">
            <code className="font-mono text-xl bg-paper-3 px-4 py-2 rounded-lg">{invite.code}</code>
            <button
              className="btn-primary"
              onClick={copyCode}
            >
              {copied ? t("common.copied") : t("common.copy")}
            </button>
          </div>
          <p className="mt-2 text-xs text-ink-3">
            {t("circles.codeHint")}: {invite.codeHint.toUpperCase()}
          </p>
          <p className="mt-2 text-xs text-ink-3">
            {t("circles.expiresAt")}: {new Date(invite.expiresAt).toLocaleString()}
          </p>
        </div>
        <button className="btn-primary w-full" onClick={onClose}>
          {t("common.done")}
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function InvitesModal({ invites, isLoading, isCreating, onClose, onCreate, onRevoke }: {
  invites: CircleInvite[];
  isLoading: boolean;
  isCreating: boolean;
  onClose: () => void;
  onCreate: (maxUses?: number) => void;
  onRevoke: (inviteId: string) => void;
}) {
  const { t } = usePrefs();
  const [maxUses, setMaxUses] = useState<string>("1");

  return (
    <Modal title={t("circles.manageInvites")} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="card p-4">
          <h3 className="mb-3 font-medium">{t("circles.createInviteCode")}</h3>
          <div className="flex gap-2 items-end">
            <label className="block flex-1">
              <span className="text-sm">{t("circles.maxUses")}</span>
              <input
                type="number"
                min="1"
                max="25"
                className="input mt-1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
              />
            </label>
            <button
              className="btn-primary"
              disabled={isCreating}
              onClick={() => onCreate(maxUses ? parseInt(maxUses) : undefined)}
            >
              {t("circles.createInviteCode")}
            </button>
          </div>
          <p className="mt-2 text-sm text-ink-3">{t("circles.inviteHint")}</p>
        </div>

        <div className="card p-4">
          <h3 className="mb-3 font-medium">{t("circles.activeInviteCodes")}</h3>
          {isLoading ? (
            <Loading label={t("common.loading")} />
          ) : invites.length === 0 ? (
            <p className="text-sm text-ink-3">{t("circles.noActiveInviteCodes")}</p>
          ) : (
            <div className="space-y-2">
              {invites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between gap-3 p-2 border-b border-rule-soft last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm">
                      {invite.codeHint.toUpperCase()}
                    </div>
                    <div className="text-xs text-ink-3">
                      {t("circles.inviteExpires")}: {new Date(invite.expiresAt).toLocaleDateString()}
                      {invite.maxUses > 1 && `
                        · ${invite.uses}/${invite.maxUses} ${t("circles.inviteUses")}`}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {invite.revokedAt ? (
                      <span className="chip text-ink-3">{t("circles.statusRevoked")}</span>
                    ) : (
                      <button
                        className="btn-ghost !px-2 !py-1 text-xs text-danger"
                        onClick={() => onRevoke(invite.id)}
                      >
                        {t("common.revoke")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button className="btn-ghost flex-1" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function LibrarySharingModal({
  settings, recipes, isTogglePending, isLoadingRecipes, acceptingId,
  onClose, onToggleSharing, onAcceptRecipe,
}:
  {
    settings: CircleLibrarySettings | undefined;
    recipes: AvailableLibraryRecipe[];
    isTogglePending: boolean;
    isLoadingRecipes: boolean;
    acceptingId: string | null | undefined;
    onClose: () => void;
    onToggleSharing: (enabled: boolean) => void;
    onAcceptRecipe: (recipeId: string) => void;
  }) {
  const { t } = usePrefs();
  const sharedCount = settings?.share?.sharedRecipeCount ?? 0;

  return (
    <Modal title={t("circles.librarySharing")} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="card p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-medium">{t("circles.librarySharing")}</h3>
              <p className="text-sm text-ink-3">{t("circles.librarySharingHint")}</p>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings?.enabled ?? false}
                onChange={(e) => onToggleSharing(e.target.checked)}
                disabled={isTogglePending}
                className="h-5 w-5"
              />
              <span className="text-sm">
                {settings?.enabled ? t("circles.librarySharingEnabled") : t("circles.librarySharingDisabled")}
              </span>
            </label>
          </div>
          {/* This is an all-or-nothing switch over the whole library, not a
              per-recipe picker. Saying how many recipes went out is the only
              confirmation that flipping it did anything. */}
          {settings?.enabled && (
            <p className="mt-2 text-sm text-ink-3">
              {t("circles.librarySharingCount").replace("{count}", String(sharedCount))}
            </p>
          )}
        </div>

        {/* NOT gated on your own sharing being enabled. What other households
            have shared with you is readable either way — hiding it behind your
            own toggle made the panel look permanently empty. */}
        <div className="card p-4">
          <h3 className="mb-1 font-medium">{t("circles.availableRecipes")}</h3>
          <p className="mb-3 text-sm text-ink-3">{t("circles.availableRecipesHint")}</p>
          {isLoadingRecipes ? (
            <Loading label={t("common.loading")} />
          ) : recipes.length === 0 ? (
            <p className="text-sm text-ink-3">{t("circles.noAvailableRecipes")}</p>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto space-y-2">
              {recipes.map((r) => (
                <div
                  key={r.recipeId}
                  className="flex items-center justify-between gap-3 p-2 border-b border-rule-soft last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{r.title}</div>
                    <div className="text-xs text-ink-3">
                      {r.servingLabel}
                      {r.totalMinutes ? ` · ${r.totalMinutes} min` : ""}
                    </div>
                  </div>
                  <button
                    className="btn-primary btn-small"
                    onClick={() => onAcceptRecipe(r.recipeId)}
                    disabled={r.alreadyAccepted || acceptingId === r.recipeId}
                  >
                    {r.alreadyAccepted ? t("common.done") : t("circles.acceptRecipe")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button className="btn-ghost flex-1" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
